import { Injectable } from '@angular/core';
import { youtubeLoadVideoSchema, youtubeRateSchema, youtubeSeekToSchema, youtubeVolumeSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { EventManagerService } from './event-manager/event-manager.service';
import { HelperManagerService } from './helper-manager.service';
type YtObject = {
  currentPlayPosition: number;
  currentState: number;
  lastEvent: number;
  lastPlayPosition: number;
  lastState: number;
  player: {
    playVideo: () => Promise<void>;
    pauseVideo: () => Promise<void>;
    stopVideo: () => Promise<void>;
    seekTo: (seconds: number, allowSeekAhead: boolean) => Promise<void>;
    mute: () => Promise<void>;
    unMute: () => Promise<void>;
    setPlaybackRate: (rate: number) => Promise<void>;
    setVolume: (volume: number) => Promise<void>;
    loadVideoById: (data: { videoId: string, startSeconds?: number, endSeconds?: number }) => Promise<void>;
  }
}
/**
 * Steuert eingebettete YouTube-Videos (`RAGAI.YOUTUBE`).
 * @remarks
 * `this.youtube` (der eigentliche Player-Handle) wird nicht von dieser Klasse selbst erzeugt, sondern erst
 * gesetzt, sobald das Event `RAGAI.YOUTUBE.playerready` vom einbettenden YouTube-Player-Skript ausgelöst wird.
 * Ruft man eine Methode dieser Klasse auf, **bevor** ein YouTube-Player im Bot vorhanden ist und bereit gemeldet
 * hat, schlägt der Aufruf fehl (`this.youtube` ist dann `undefined`).
 */
@Injectable({
  providedIn: 'root'
})
export class YoutubeManagerService {
  public youtube!: YtObject;
  constructor(private eventManager: EventManagerService, private ragAiHelper: HelperManagerService
  ) {
    this.eventManager.on('RAGAI.YOUTUBE.playerready', (_event, youtubeObj) => {
      this.youtube = youtubeObj as YtObject;
    });
  }
  /** Startet die Wiedergabe des YouTube-Videos. */
  public async play() {
    this.eventManager.trigger('RAGAI.YOUTUBE.PLAY', {});
    await this.youtube.player.playVideo();
  }

  /** Pausiert die Wiedergabe des YouTube-Videos. */
  public async pause() {
    this.eventManager.trigger('RAGAI.YOUTUBE.PAUSE', {});
    await this.youtube.player.pauseVideo();
  }

  /** Stoppt die Wiedergabe des YouTube-Videos. */
  public async stop() {
    this.eventManager.trigger('RAGAI.YOUTUBE.STOP', {});
    await this.youtube.player.stopVideo();
  }

  /** Schaltet den Ton des YouTube-Videos stumm. */
  public async mute() {
    this.eventManager.trigger('RAGAI.YOUTUBE.MUTE', {});
    await this.youtube.player.mute();
  }

  /** Schaltet den Ton des YouTube-Videos wieder ein. */
  public async unmute() {
    this.eventManager.trigger('RAGAI.YOUTUBE.UNMUTE', {});
    await this.youtube.player.unMute();
  }

  /**
   * Setzt die Wiedergabegeschwindigkeit des YouTube-Videos.
   * @param rate Wiedergabegeschwindigkeit (1 = normal), oder ein Objekt gemäß `youtubeRateSchema`.
   */
  public async setPlaybackRate(rate: number | z.input<typeof youtubeRateSchema>) {
    const parsed = youtubeRateSchema.safeParse(rate);
    if (parsed.success && typeof rate === 'object') {
      rate = parsed.data.rate;
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.YOUTUBE.SETPLAYBACKRATE', {});
    await this.youtube.player.setPlaybackRate(rate as number);
  }

  /**
   * Setzt die Lautstärke des YouTube-Videos.
   * @param volume Lautstärke in Prozent (0-100), oder ein Objekt gemäß `youtubeVolumeSchema`.
   */
  public async setVolume(volume: number | z.input<typeof youtubeVolumeSchema>) {
    const parsed = youtubeVolumeSchema.safeParse(volume);
    if (parsed.success && typeof volume === 'object') {
      volume = parsed.data.volume;
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.YOUTUBE.SETVOLUME', {});
    await this.youtube.player.setVolume(volume as number);
  }

  /**
   * Springt zu einer bestimmten Position im YouTube-Video.
   * @param hours Stunden, oder ein Objekt gemäß `youtubeSeekToSchema`.
   * @param minutes Minuten.
   * @param seconds Sekunden.
   * @param allowSeekAhead `true`, um auch in noch nicht geladene Bereiche springen zu können.
   */
  public async seekTo(hours: number | z.input<typeof youtubeSeekToSchema>, minutes: number = 0, seconds: number = 0, allowSeekAhead = true) {
    const parsed = youtubeSeekToSchema.safeParse(hours);
    if (parsed.success && typeof hours === 'object') {
      minutes = parsed.data.minutes;
      seconds = parsed.data.seconds;
      hours = parsed.data.hours;
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.YOUTUBE.SEEKTO', { seconds });
    seconds = this.ragAiHelper.timeToSeconds(hours as number, minutes, seconds);
    await this.youtube.player.seekTo(seconds, allowSeekAhead);
  }

  /**
   * Lädt ein anderes YouTube-Video in den Player.
   * @param videoId Die YouTube-Video-ID, oder ein Objekt gemäß `youtubeLoadVideoSchema`.
   * @param startSeconds Startzeitpunkt in Sekunden.
   * @param endSeconds Endzeitpunkt in Sekunden.
   */
  public async loadVideo(videoId: string | z.input<typeof youtubeLoadVideoSchema>, startSeconds?: number, endSeconds?: number) {
    const parsed = youtubeLoadVideoSchema.safeParse(videoId);
    if (parsed.success && typeof videoId === 'object') {
      videoId = parsed.data.videoId;
    }
    if (startSeconds && startSeconds < 1) startSeconds = undefined;
    if (endSeconds && endSeconds < 1) endSeconds = undefined;
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.YOUTUBE.CHANGEVIDEO', {});
    await this.youtube.player.loadVideoById({ videoId: videoId as string, startSeconds, endSeconds });
  }
}
